/**
 * TaskPrefillPanel — Right-side drawer for LLM auto-fill of ComprehensiveTaskForm.
 *
 * Input: Textarea for context (paste a document / describe the task).
 * Results: Suggestion cards grouped by tab, each with confidence bar, PII badge,
 *          accept/reject toggle.
 * Bulk actions: Accept High Confidence, Accept All, Reject All.
 * Apply: Distributes accepted suggestions to form setters via applyFormState.
 */

import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Drawer,
  Group,
  Loader,
  Paper,
  Progress,
  ScrollArea,
  Stack,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';
import {
  IconCheck,
  IconInfoCircle,
  IconSparkles,
  IconX,
} from '@tabler/icons-react';
import React, { useMemo } from 'react';
import type { TaskFormSetters } from './taskFormState';
import { applyFormState, TASK_FIELD_MANIFEST } from './taskFormState';
import {
  useLlmTaskPrefill,
  type TaskPrefillSuggestion,
  type SuggestionDecision,
} from '../hooks/useLlmTaskPrefill';

// ============================================================================
// Types
// ============================================================================

export interface TaskPrefillPanelProps {
  opened: boolean;
  onClose: () => void;
  setters: Partial<TaskFormSetters>;
  onApplied?: () => void;
}

// ============================================================================
// Helpers
// ============================================================================

function confidenceColor(confidence: number): string {
  if (confidence >= 0.85) return 'green';
  if (confidence >= 0.6) return 'yellow';
  return 'red';
}

function getTabForField(fieldName: string): string {
  const entry = TASK_FIELD_MANIFEST.find(f => f.name === fieldName);
  return entry?.tab ?? 'other';
}

const TAB_LABELS: Record<string, string> = {
  core: 'Core',
  hierarchy: 'Hierarchy',
  security: 'Security',
  escalation: 'Escalation',
  compliance: 'Compliance',
  advanced: 'Advanced',
  other: 'Other',
};

// ============================================================================
// Suggestion Card
// ============================================================================

interface SuggestionCardProps {
  fieldName: string;
  value: unknown;
  confidence: number;
  explanation: string;
  isPii: boolean;
  decision: SuggestionDecision;
  onAccept: () => void;
  onReject: () => void;
}

const SuggestionCard: React.FC<SuggestionCardProps> = ({
  fieldName,
  value,
  confidence,
  explanation,
  isPii,
  decision,
  onAccept,
  onReject,
}) => {
  const displayValue = typeof value === 'object' ? JSON.stringify(value, null, 1) : String(value);
  const truncated = displayValue.length > 80 ? displayValue.slice(0, 80) + '...' : displayValue;

  return (
    <Paper
      p="xs"
      withBorder
      style={{
        borderLeftWidth: 3,
        borderLeftColor:
          decision === 'accepted'
            ? 'var(--mantine-color-green-6)'
            : decision === 'rejected'
              ? 'var(--mantine-color-red-4)'
              : 'var(--mantine-color-gray-4)',
        opacity: decision === 'rejected' ? 0.6 : 1,
      }}
    >
      <Group justify="space-between" wrap="nowrap" mb={2}>
        <Group gap={6}>
          <Text size="xs" fw={600} tt="capitalize">
            {fieldName.replace(/([A-Z])/g, ' $1').trim()}
          </Text>
          {isPii && (
            <Badge size="xs" color="red" variant="filled">
              PII
            </Badge>
          )}
        </Group>
        <Group gap={4}>
          <Tooltip label="Accept" withArrow>
            <ActionIcon
              size="xs"
              color="green"
              variant={decision === 'accepted' ? 'filled' : 'subtle'}
              onClick={onAccept}
            >
              <IconCheck size={12} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Reject" withArrow>
            <ActionIcon
              size="xs"
              color="red"
              variant={decision === 'rejected' ? 'filled' : 'subtle'}
              onClick={onReject}
            >
              <IconX size={12} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      <Text size="xs" c="dimmed" lineClamp={2} mb={4}>
        {truncated}
      </Text>

      <Group gap="xs" ta="center">
        <Progress
          value={confidence * 100}
          size="xs"
          color={confidenceColor(confidence)}
          style={{ flex: 1 }}
        />
        <Text size="xs" c="dimmed">
          {Math.round(confidence * 100)}%
        </Text>
      </Group>

      {explanation && (
        <Text size="xs" c="dimmed" mt={2} fs="italic">
          {explanation}
        </Text>
      )}
    </Paper>
  );
};

// ============================================================================
// Main Panel
// ============================================================================

export const TaskPrefillPanel: React.FC<TaskPrefillPanelProps> = ({
  opened,
  onClose,
  setters,
  onApplied,
}) => {
  const prefill = useLlmTaskPrefill();
  const { state, setContextText, generate, acceptSuggestion, rejectSuggestion } = prefill;

  // Group suggestions by form tab
  const groupedSuggestions = useMemo(() => {
    const groups: Record<string, typeof state.suggestions> = {};
    for (const s of state.suggestions) {
      const tab = getTabForField(s.fieldName);
      if (!groups[tab]) groups[tab] = [];
      groups[tab].push(s);
    }
    return groups;
  }, [state.suggestions]);

  const handleApply = () => {
    const accepted = prefill.getAcceptedState();
    applyFormState(accepted, setters);
    onApplied?.();
    onClose();
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconSparkles size={20} />
          <Text fw={600}>LLM Auto-Fill</Text>
        </Group>
      }
      position="right"
      size="lg"
    >
      <Stack gap="md" h="calc(100vh - 80px)">
        {/* Input area */}
        <Textarea
          label="Paste context to auto-fill form fields"
          placeholder="Paste a document, email, or describe the task you want to create..."
          rows={4}
          value={state.contextText}
          onChange={(e) => setContextText(e.currentTarget.value)}
        />

        <Button
          leftSection={state.isGenerating ? <Loader size={14} /> : <IconSparkles size={16} />}
          onClick={generate}
          disabled={!state.contextText.trim() || state.isGenerating}
          fullWidth
        >
          {state.isGenerating ? 'Generating...' : 'Generate Suggestions'}
        </Button>

        {state.error && (
          <Alert color="red" icon={<IconInfoCircle size={16} />}>
            {state.error}
          </Alert>
        )}

        {/* Results */}
        {state.suggestions.length > 0 && (
          <>
            {/* Bulk actions */}
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                {state.suggestions.length} suggestions &middot;{' '}
                <Text span c="green" fw={500}>{prefill.acceptedCount} accepted</Text>{' '}
                <Text span c="red">{prefill.rejectedCount} rejected</Text>{' '}
                <Text span c="gray">{prefill.pendingCount} pending</Text>
              </Text>
              <Group gap={4}>
                <Button
                  size="xs"
                  variant="light"
                  color="green"
                  onClick={() => prefill.acceptHighConfidence()}
                >
                  Accept High (85%+)
                </Button>
                <Button size="xs" variant="light" onClick={() => prefill.acceptAll()}>
                  Accept All
                </Button>
                <Button
                  size="xs"
                  variant="light"
                  color="red"
                  onClick={() => prefill.rejectAll()}
                >
                  Reject All
                </Button>
              </Group>
            </Group>

            {/* Suggestion cards grouped by tab */}
            <ScrollArea style={{ flex: 1 }} offsetScrollbars>
              <Stack gap="md">
                {Object.entries(groupedSuggestions).map(([tab, suggestions]) => (
                  <div key={tab}>
                    <Text size="xs" fw={700} tt="uppercase" c="dimmed" mb={4}>
                      {TAB_LABELS[tab] || tab}
                    </Text>
                    <Stack gap={6}>
                      {suggestions.map((s) => (
                        <SuggestionCard
                          key={s.fieldName}
                          fieldName={s.fieldName}
                          value={s.suggestedValue}
                          confidence={s.confidence}
                          explanation={s.explanation}
                          isPii={s.isPii}
                          decision={s.decision}
                          onAccept={() => acceptSuggestion(s.fieldName)}
                          onReject={() => rejectSuggestion(s.fieldName)}
                        />
                      ))}
                    </Stack>
                  </div>
                ))}
              </Stack>
            </ScrollArea>

            {/* Apply button */}
            <Button
              size="lg"
              color="green"
              fullWidth
              leftSection={<IconCheck size={18} />}
              onClick={handleApply}
              disabled={prefill.acceptedCount === 0}
            >
              Apply {prefill.acceptedCount} Accepted Fields
            </Button>
          </>
        )}
      </Stack>
    </Drawer>
  );
};

export default TaskPrefillPanel;
